import type { MemoryType, UpdateMemoryInput } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useState } from 'react';

import { useApproveMemory, useUpdateMemory } from '../../lib/agent-memory';
import {
  BindingTargetSelect,
  type IBindingTargets,
  type INodeTarget,
} from '../common/BindingTargetSelect';
import { Modal } from '../common/Modal';

import { dateInputToIso, isoToDateInput } from './memory-date';

/**
 * The memory's binding set as it stands, for the optional scope editor.
 * Callers that don't have binding data loaded (e.g. a chat card whose live
 * row hasn't resolved yet) omit the prop and the scope section is hidden —
 * the modal then behaves exactly as before scope editing existed.
 */
export interface IEditableScope {
  appliesToAll: boolean;
  agentIds: string[];
  collectionIds: string[];
  /** Workflow LLM-node targets the memory is bound to (DEV-17157). */
  nodeBindings: INodeTarget[];
  /**
   * A subjectRef-scoped memory — an advanced "about-ness" filter (e.g.
   * `{ workflowId }`) that narrows WITHIN an already-resolved set. Distinct from
   * a node binding (which grants access to a node). Shown read-only here — it is
   * an advanced field with no editor yet.
   */
  subjectScoped?: boolean;
}

type ScopeMode = 'all' | 'targets';

function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every((id) => bs.has(id));
}

const nodeKeyOf = (t: INodeTarget) => `${t.workflowId}|${t.nodeKey}`;
function sameNodeSet(a: readonly INodeTarget[], b: readonly INodeTarget[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b.map(nodeKeyOf));
  return a.every((t) => bs.has(nodeKeyOf(t)));
}

/**
 * Whether the operator's scope choice differs from the memory's current
 * binding. Mirrors the chat card's inline flip semantics: switching to
 * org-wide sends ONLY `appliesToAll: true` (existing bindings are kept, so
 * narrowing back later starts from where the agent proposed); switching to
 * specific targets sends the full explicit binding set.
 */
export function isScopeDirty(
  initial: IEditableScope,
  mode: ScopeMode,
  targets: IBindingTargets,
): boolean {
  if (initial.subjectScoped) return false;
  if ((mode === 'all') !== initial.appliesToAll) return true;
  if (mode === 'all') return false;
  return (
    !sameIdSet(targets.agentIds, initial.agentIds) ||
    !sameIdSet(targets.collectionIds, initial.collectionIds) ||
    !sameNodeSet(targets.nodeBindings, initial.nodeBindings)
  );
}

/** The PATCH fields for a scope change (empty object when scope is clean). */
export function buildScopePatch(
  initial: IEditableScope,
  mode: ScopeMode,
  targets: IBindingTargets,
): Pick<UpdateMemoryInput, 'appliesToAll' | 'agentIds' | 'collectionIds' | 'nodeBindings'> {
  if (!isScopeDirty(initial, mode, targets)) return {};
  if (mode === 'all') return { appliesToAll: true };
  return {
    appliesToAll: false,
    agentIds: targets.agentIds,
    collectionIds: targets.collectionIds,
    nodeBindings: targets.nodeBindings,
  };
}

/** Compact one-line description of the current binding for the collapsed row. */
export function scopeSummary(scope: IEditableScope): string {
  if (scope.subjectScoped) return 'Workflow-scoped';
  if (scope.appliesToAll) return 'All agents in the org';
  const parts: string[] = [];
  if (scope.collectionIds.length > 0) {
    parts.push(
      `${scope.collectionIds.length} collection${scope.collectionIds.length === 1 ? '' : 's'}`,
    );
  }
  if (scope.agentIds.length > 0) {
    parts.push(`${scope.agentIds.length} agent${scope.agentIds.length === 1 ? '' : 's'}`);
  }
  if (scope.nodeBindings.length > 0) {
    parts.push(`${scope.nodeBindings.length} node${scope.nodeBindings.length === 1 ? '' : 's'}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Unbound';
}

/**
 * The "Applies to" row of the edit modal. Collapsed (summary + Change-scope
 * button) by default so the common path — fix the wording, approve — never
 * fronts an agent list; expanding reveals org-wide vs specific-targets, with
 * the specific mode reusing the BindingTargetSelect search typeahead so
 * scoping against a large fleet is a search, not a scroll. Exported for
 * direct testing (the Modal portals, so it is invisible to static render).
 */
export function MemoryScopeField({
  initialScope,
  open,
  onOpen,
  mode,
  onModeChange,
  targets,
  onTargetsChange,
  scopeValid,
}: {
  initialScope: IEditableScope;
  open: boolean;
  onOpen: () => void;
  mode: ScopeMode;
  onModeChange: (mode: ScopeMode) => void;
  targets: IBindingTargets;
  onTargetsChange: (targets: IBindingTargets) => void;
  scopeValid: boolean;
}) {
  const widening = mode === 'all' && !initialScope.appliesToAll;
  return (
    <div className="field">
      <span className="field-label">Applies to</span>
      {initialScope.subjectScoped ? (
        <span className="t-xs t-muted">
          Workflow-scoped — the binding is managed in the workflow node panel.
        </span>
      ) : !open ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <span className="t-xs">{scopeSummary(initialScope)}</span>
          <button type="button" className="btn btn--xs btn--ghost" onClick={onOpen}>
            Change scope
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <div
            role="radiogroup"
            aria-label="Memory scope"
            style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}
          >
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
              <input
                type="radio"
                name="memory-scope"
                checked={mode === 'all'}
                onChange={() => onModeChange('all')}
              />
              <span className="t-xs">All agents in the org</span>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
              <input
                type="radio"
                name="memory-scope"
                checked={mode === 'targets'}
                onChange={() => onModeChange('targets')}
              />
              <span className="t-xs">Specific agents, collections, or workflow nodes</span>
            </label>
          </div>

          {mode === 'targets' ? (
            <>
              <BindingTargetSelect value={targets} onChange={onTargetsChange} includeNodes={true} />
              <span className="t-xs t-muted">
                ◆ A collection covers every current and future member agent. ⬡ A workflow node
                scopes this memory to one llm-agent node in a workflow.
              </span>
              {!scopeValid ? (
                <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                  Pick at least one agent, collection, or workflow node.
                </span>
              ) : null}
            </>
          ) : widening ? (
            <span className="t-xs" style={{ color: 'var(--status-warning)' }}>
              Widening scope: once approved, this memory is injected into every agent in the org.
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Edit-before-approving modal for an agent-proposed memory awaiting review
 * (PENDING or CANDIDATE), against the unified /v1/memories API (DEV-16962).
 *
 * A DISTINCT component from `components/refinement/EditMemoryModal` — that
 * one stays theopolis-backed for the in-thread `MemoryToolCard` (a separate
 * data model/id space, out of this migration's scope). Only the standalone
 * `/agents/memories` page (via `MemoryListRow`) and the PA chat
 * `AgentMemoryCard` use this one.
 *
 * Two-step server flow under a single operator interaction:
 *   1. `PATCH /v1/memories/:id` (copy-on-write, new id+version — status
 *      carries forward from the old row, so an edited PENDING/CANDIDATE row
 *      is still awaiting review)
 *   2. `POST /v1/memories/:newId/approve`
 *
 * Reused unchanged for the standalone page's Edit action on already-APPROVED
 * memories — `skipApprove` wires that up (skip the second step).
 *
 * Scope: when `initialScope` is provided, an "Applies to" row shows the
 * current binding with a Change-scope affordance. Collapsed by default —
 * the reviewer's common edit is the content, and scope review is opt-in so
 * the modal never fronts a large agent list. Expanding reveals an org-wide
 * vs specific-targets choice; specific targets reuse BindingTargetSelect
 * (search typeahead over agents AND collections), so scoping against a big
 * fleet is a search, not a scroll.
 */
export function EditUnifiedMemoryModal({
  memoryId,
  initial,
  initialScope,
  skipApprove = false,
  onClose,
  onSaved,
  onError,
}: {
  memoryId: string;
  initial: {
    content: string;
    type: MemoryType;
    priority: number;
    expiresAt: string | Date | null;
  };
  /** Current binding set; omit (data not loaded) to hide the scope section. */
  initialScope?: IEditableScope;
  /** When true, only PATCH — don't approve afterwards. Standalone page uses this. */
  skipApprove?: boolean;
  onClose: () => void;
  /**
   * Called with the id of the row the edit actually landed on. Copy-on-write
   * means this is a NEW id, not `memoryId` — callers that keep their own
   * reference to a memory (e.g. a chat transcript card pinned to one id
   * extracted from a historical tool call) MUST switch to this id, or they'll
   * keep querying the old row, which is now archived/superseded and will look
   * permanently "Archived" even though the edit succeeded. List-driven
   * callers (e.g. the standalone memories page) can safely ignore the arg —
   * their list query already drops the old row and picks up the new one.
   */
  onSaved: (newId: string) => void;
  onError: (message: string) => void;
}) {
  const [content, setContent] = useState(initial.content);
  const [type, setType] = useState<MemoryType>(initial.type);
  const [priority, setPriority] = useState<0 | 1 | 2>(initial.priority as 0 | 1 | 2);
  const [expiresAt, setExpiresAt] = useState<string>(
    initial.expiresAt ? isoToDateInput(initial.expiresAt) : '',
  );

  const scopeEditable = initialScope != null && !initialScope.subjectScoped;
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeMode, setScopeMode] = useState<ScopeMode>(
    initialScope?.appliesToAll ? 'all' : 'targets',
  );
  // Seeded from the current binding so narrowing/widening starts from what
  // the agent proposed, not a blank slate.
  const [targets, setTargets] = useState<IBindingTargets>({
    agentIds: initialScope?.agentIds ?? [],
    collectionIds: initialScope?.collectionIds ?? [],
    nodeBindings: initialScope?.nodeBindings ?? [],
  });

  const update = useUpdateMemory();
  const approve = useApproveMemory();

  const initialExpiresAtInput = initial.expiresAt ? isoToDateInput(initial.expiresAt) : '';
  const fieldsDirty =
    content.trim() !== initial.content.trim() ||
    type !== initial.type ||
    priority !== initial.priority ||
    expiresAt !== initialExpiresAtInput;
  const scopeDirty = scopeEditable && isScopeDirty(initialScope, scopeMode, targets);
  const dirty = fieldsDirty || scopeDirty;

  // A "specific targets" scope must bind at least one agent or collection —
  // the store never allows an orphaned memory (same rule as create).
  const scopeValid =
    !scopeEditable ||
    scopeMode === 'all' ||
    targets.agentIds.length > 0 ||
    targets.collectionIds.length > 0 ||
    targets.nodeBindings.length > 0;

  async function onSave() {
    if (!content.trim()) {
      onError('Memory content cannot be empty.');
      return;
    }
    try {
      const patch: UpdateMemoryInput = {
        content: content.trim(),
        type,
        priority,
        // Three-way: '' clears (null), value sets. We never send `undefined`
        // from this form — the modal always reflects an explicit choice.
        expiresAt: dateInputToIso(expiresAt),
        // Scope fields ride along only when the reviewer changed them —
        // omitted fields leave the binding untouched server-side.
        ...(scopeEditable ? buildScopePatch(initialScope, scopeMode, targets) : {}),
      };
      const updated = await update.mutateAsync({ id: memoryId, patch });
      if (!skipApprove) {
        await approve.mutateAsync(updated.id);
      }
      onSaved(updated.id);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  const saving = update.isPending || approve.isPending;

  return (
    <Modal
      open={true}
      onClose={onClose}
      eyebrow="Memory"
      title={skipApprove ? 'Edit memory' : 'Edit before approving'}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => fork(onSave())}
            disabled={saving || !dirty || !content.trim() || !scopeValid}
          >
            {saving ? 'Saving…' : skipApprove ? 'Save changes' : 'Save & approve'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <label className="field">
          <span className="field-label">Memory</span>
          <textarea
            className="textarea"
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What should the agent remember?"
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <label className="field">
            <span className="field-label">Type</span>
            <select
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value as MemoryType)}
            >
              <option value="RULE">rule</option>
              <option value="PREFERENCE">preference</option>
              <option value="CONTEXT">context</option>
              <option value="NOTE">note</option>
              <option value="PRINCIPLE">principle</option>
            </select>
          </label>

          <label className="field">
            <span className="field-label">Priority</span>
            <select
              className="input"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) as 0 | 1 | 2)}
            >
              <option value={0}>normal</option>
              <option value={1}>important</option>
              <option value={2}>pinned</option>
            </select>
          </label>
        </div>

        {initialScope ? (
          <MemoryScopeField
            initialScope={initialScope}
            open={scopeOpen}
            onOpen={() => setScopeOpen(true)}
            mode={scopeMode}
            onModeChange={setScopeMode}
            targets={targets}
            onTargetsChange={setTargets}
            scopeValid={scopeValid}
          />
        ) : null}

        <label className="field">
          <span className="field-label">Expires (optional)</span>
          <input
            type="date"
            className="input"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
          <span className="t-xs t-muted" style={{ marginTop: 'var(--sp-1)' }}>
            Leave blank for no expiry. Past dates mark the memory stale and exclude it from
            injection.
          </span>
        </label>
      </div>
    </Modal>
  );
}
